/**
 * AnalyticsContext-v2 - GA4 event tracking for Ivy Astrology
 *
 * This is a v2 replacement that provides:
 * - A generic trackEvent() function for custom GA4 events
 * - Pre-defined analytics methods matching the measurement plan
 * - React context + hook for component-level access
 * - Console logging in development for debugging
 *
 * Usage:
 *   import { analytics } from '../context/AnalyticsContext-v2';
 *   analytics.wizardStart();
 *   analytics.purchase('Natal Chart', 29.99, 'txn_abc123');
 *
 * Or via React context:
 *   import { useAnalyticsV2 } from '../context/AnalyticsContext-v2';
 *   const { trackEvent, analytics } = useAnalyticsV2();
 */

import React, { createContext, useContext, useMemo } from 'react';

// ---------------------------------------------------------------------------
// Core tracking function
// ---------------------------------------------------------------------------

/**
 * Send a GA4 event. Safe to call even if gtag is not loaded (e.g., ad-blocker).
 *
 * @param {string}  eventName - GA4 event name (snake_case)
 * @param {Object}  params    - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  // Development logging
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics]', eventName, params);
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// ---------------------------------------------------------------------------
// Pre-defined events matching the Ivy Astrology measurement plan
// ---------------------------------------------------------------------------

export const analytics = {
  // -- Wizard / Onboarding --
  wizardStart: () => trackEvent('wizard_start'),

  lifeAreaSelected: (area) =>
    trackEvent('life_area_selected', { life_area: area }),

  birthTimeConfidence: (level) =>
    trackEvent('birth_time_confidence', { confidence: level }),

  // KEY EVENT: user reveals the free preview of their reading
  previewRevealed: () => trackEvent('preview_revealed'),

  // -- Opt-ins --
  emailOptIn: () => trackEvent('email_opt_in'),

  whatsappOptIn: () => trackEvent('whatsapp_opt_in'),

  // -- Product funnel --
  productRecommended: (product) =>
    trackEvent('product_recommended', { product_name: product }),

  viewProduct: (product, price) =>
    trackEvent('view_product', { product_name: product, price }),

  beginCheckout: (product, price) =>
    trackEvent('begin_checkout', {
      product_name: product,
      value: price,
      currency: 'EUR',
    }),

  purchase: (product, price, transactionId) =>
    trackEvent('purchase', {
      transaction_id: transactionId,
      value: price,
      currency: 'EUR',
      items: [{ item_name: product }],
    }),

  // -- Subscriptions --
  subscriptionStart: (tier) =>
    trackEvent('subscription_start', { tier }),

  // -- Sessions --
  sessionBooked: () => trackEvent('session_booked'),

  // -- Gifting --
  giftPurchase: (product, price) =>
    trackEvent('gift_purchase', { product_name: product, value: price }),

  // -- Compatibility --
  compatibilityStarted: () => trackEvent('compatibility_started'),

  compatibilityInviteSent: () => trackEvent('compatibility_invite_sent'),

  // -- Sharing --
  cardShared: (channel) =>
    trackEvent('card_shared', { share_channel: channel }),

  referralShared: () => trackEvent('referral_link_shared'),

  // -- Retention --
  repeatPurchase: (product) =>
    trackEvent('repeat_purchase', { product_name: product }),
};

// ---------------------------------------------------------------------------
// React Context (optional -- allows injection via provider)
// ---------------------------------------------------------------------------

const AnalyticsV2Context = createContext(null);

/**
 * Hook to access analytics tracking from within a React component tree.
 * Falls back gracefully if used outside the provider.
 */
export const useAnalyticsV2 = () => {
  const ctx = useContext(AnalyticsV2Context);
  // Allow usage outside provider for convenience (uses module-level functions)
  return ctx || { trackEvent, analytics };
};

/**
 * Provider component. Wrap your app (or a subtree) to make analytics
 * available via useAnalyticsV2().
 */
export function AnalyticsV2Provider({ children }) {
  const value = useMemo(() => ({ trackEvent, analytics }), []);

  return (
    <AnalyticsV2Context.Provider value={value}>
      {children}
    </AnalyticsV2Context.Provider>
  );
}

export default AnalyticsV2Provider;
